import CommonModule, { QuestelServicesClient, RequestResult, HttpMethodEnum } from '@questelservices/common';
import store from '@/store';
import { LoadData } from '@/models/Pagination/LoadData';
import PageList from '@/models/Pagination/PageList';
import { Order } from '@/models/Orders/Order';
import { OrderStatus } from '@/models/Orders/OrderStatus';
import OrderFilter from '@/models/Orders/OrderFilter';
import { OrderStatusUpdate } from '@/models/Orders/OrderStatusUpdate';
import { DeclarationOptions } from '@/models/Orders/DeclarationOptions'
import { getModule } from 'vuex-module-decorators';
import { AddComment } from '../models/AddComment';
import { DownloadDocumentOptOut } from '../models/DownloadDocumentOptOut';

export class OrderService {
  private client: QuestelServicesClient;
  private commonState = getModule(CommonModule, store);

  constructor() {
    this.client = new QuestelServicesClient(store, "/api");
  }

  public async getOrders(dataQuery: LoadData, filter: OrderFilter): Promise<PageList<Order>> {
    const queryString = Object.keys(dataQuery).filter(prop => dataQuery[prop])
                      .map(key => {
                        return `${key}=${encodeURIComponent(dataQuery[key])}`
                      })
                      .join('&');
      const response = await this.client.getPostData<PageList<Order>>(`/v1/orders/OrderList?${queryString}`, filter);

    return {
      Data: response.Data,
      PageNumber: response.PageNumber,
      PageSize: response.PageSize,
      TotalItemCount: response.TotalItemCount
    }
  }

  public async addOrder(order: Order): Promise<RequestResult<Order>> {
    return await this.client.postJson<Order>(`/v1/orders`, order);
  }

  public async updateOrder(order: Order): Promise<RequestResult<Order>> {
    return await this.client.putJson<Order>(`/v1/orders`, order);
  }

  public async getOrderStatuses(): Promise<OrderStatus[]> {
    return await this.client.get<OrderStatus[]>(`/v1/orders/StatusList`);
    }

    public async getDeclarationOptions(): Promise<DeclarationOptions[]> {
        return await this.client.get<DeclarationOptions[]>(`/v1/orders/declarationOptions`);
    }

  public async getOrder(orderId: Guid): Promise<Order> {
    return await this.client.get<Order>(`/v1/orders/${orderId}`);
    }


  public async updateOrderStatus(updateOrderStatus: OrderStatusUpdate): Promise<RequestResult<Order>> {
    return await this.client.postJson<Order>(`/v1/orders/order-status`, updateOrderStatus);
    }

    public async getOrderContract(orderId: Guid) : Promise<any> {
        var request = new Request(`/api/v1/orders/DownloadContract/${orderId}`, {
            method: HttpMethodEnum.Get,
            headers: {
              "Authorization": `Bearer ${this.commonState.authToken}`,
            }
        });

        return fetch(request);
    }

    public async addComment(item: AddComment): Promise<RequestResult<boolean>> {
        return await this.client.postJson<boolean>(`/v1/orders/AddComment`, item);
    }

    public async retryOrder(order: Order): Promise<Order> {
        return await this.client.get<Order>(`/v1/orders/retryOrder/${order.Id}`);
    }

    public async downloadOptOutDocument(token: Guid): Promise<any> {
        var request = new Request(`/api/v1/ReceiptDownload/DownloadOptOutDocuments/${token}`, {
            method: HttpMethodEnum.Get,
            headers: {
                "Authorization": `Bearer ${this.commonState.authToken}`,
            },
        });

        return fetch(request);
    }

    public async downloadOptOutDocuments(orderId: Guid): Promise<any> {
        return await this.client.get<DownloadDocumentOptOut>(`/v1/orders/DownloadOptOutDocuments/${orderId}`);
    }

    public async resetDownloadOptOutDocuments(orderId: Guid): Promise<any> {
        return await this.client.get<boolean>(`/v1/orders/ResetDownloadOptOutDocuments/${orderId}`);
    }

    public async getOrderCaseContract(contractId: Guid): Promise<any> {
        var request = new Request(`/api/v1/orders/DownloadCasesContract/${contractId}`, {
            method: HttpMethodEnum.Get,
            headers: {
              "Authorization": `Bearer ${this.commonState.authToken}`,
            },
        });
        
        const fetchPromise = fetch(request);

        return fetchPromise;
    }

    public async getAllOrderContract(orderId: Guid): Promise<any> {
        var request = new Request(`/api/v1/orders/DownloadAllContract/${orderId}`, {
            method: HttpMethodEnum.Get,
            headers: {
              "Authorization": `Bearer ${this.commonState.authToken}`,
            }
        });

        return fetch(request);
    }

    public async getOrderReceipts(orderId: Guid): Promise<any> {
        var request = new Request(`/api/v1/orders/DownloadReceipts/${orderId}`, {
            method: HttpMethodEnum.Get,
            headers: {
                "Authorization": `Bearer ${this.commonState.authToken}`,
            },
            keepalive: true
        });

        return fetch(request);
    }

    public async getReport(OrderID: Guid) {
        const form = new FormData();
        form.append('OrderID', OrderID);
        var id = OrderID;
        var request = new Request(`/api/v1/orders/ExcelReport`, {
            method: HttpMethodEnum.Post,
            body: form,
            headers: {
              "Authorization": `Bearer ${this.commonState.authToken}`,
            }
        });

        return fetch(request);
    }

    public async generateLink(OrderID: Guid): Promise<any> {
        return await this.client.postJson<Order>(`/v1/ReceiptDownload/${OrderID}/GenerateToken`, OrderID);
    }
}